import { useEffect, useState } from "react";

/**
 * How much motion this device gets, decided once per session (no WebGL context is created to find out):
 *   "static"  reduced motion, data saver, low memory (< 3 GB) or few cores (< 4): still frames, no float, no scroll linking
 *   "lite"    other touch devices: short scroll scenes, at most two floating cards, no pointer tilt
 *   "full"    a mouse or trackpad: everything
 * The low-end checks are the same ones lib/webgl.js autoGhostAllowed uses for the 3D fix.
 */
let memo = null;

export function motionTier() {
  if (memo) return memo;
  try {
    const c = navigator.connection;
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const weak = (typeof navigator.deviceMemory === "number" && navigator.deviceMemory < 3) || (typeof navigator.hardwareConcurrency === "number" && navigator.hardwareConcurrency < 4);
    if (calm || c?.saveData || weak) memo = "static";
    else memo = window.matchMedia("(hover: hover) and (pointer: fine)").matches ? "full" : "lite";
  } catch {
    memo = "lite";
  }
  if (process.env.NODE_ENV !== "production") console.info("[motion] tier:", memo);
  return memo;
}

/** The tier as a hook: also drops to "static" if the person turns reduced motion on while the page is open. */
export function useMotionTier() {
  const [tier, setTier] = useState(motionTier);
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(prefers-reduced-motion: reduce)"); } catch { return undefined; }
    const on = () => { if (mq.matches) { memo = "static"; setTier("static"); } };
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return tier;
}
